import { addDays, differenceInCalendarDays } from 'date-fns';

import { parseUseDayBookings } from '@shared/functions/parse-use-day-bookings';
import { DefaultBerthReservationTimeUnit } from '@shared/models/bcm-settings';

/**
 * Settings round-trip through varchar, so a time part arrives as a number, a
 * numeric string, or '' when unset. Only a truly-unset value falls back — a
 * configured 0 (e.g. `:00` minutes, midnight) is preserved. (The legacy callers
 * used `value || fallback`, which wrongly turned a configured `:00` into `:59`.)
 */
function timePart(value: number | string | null | undefined, fallback: number): number {
    if (value === null || value === undefined || value === '') {
        return fallback;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? fallback : parsed;
}

/**
 * Seeds a booking's from/to with the marina's configured default check-in/out
 * times: same-day ranges use the daily-guest ("untertägig") times, multi-day
 * ranges the overnight-guest ("Übernachtung") times, and an open end always uses
 * the overnight check-in time. When same-day bookings are disabled a same-day
 * range rolls its end to the next day. Pure — the inputs are cloned, not mutated.
 * Used when a booking is created. The berth map uses its own day window.
 */
export function applyReservationDefaultTimes(
    from: Date,
    to: Date | null,
    unit: DefaultBerthReservationTimeUnit,
): { from: Date; to: Date | null } {
    const {
        dailyGuestFromHours,
        dailyGuestFromMinutes,
        dailyGuestToHours,
        dailyGuestToMinutes,
        overnightGuestFromHours,
        overnightGuestFromMinutes,
        overnightGuestToHours,
        overnightGuestToMinutes,
        useDayBookings,
    } = unit;

    const start = new Date(from);
    let end = to ? new Date(to) : null;

    const dayBookingsEnabled = parseUseDayBookings(useDayBookings);
    const differenceInDays = end ? differenceInCalendarDays(end, start) : null;

    if (!end) {
        start.setHours(timePart(overnightGuestFromHours, 0), timePart(overnightGuestFromMinutes, 0), 0, 0);
    } else if (dayBookingsEnabled && differenceInDays === 0) {
        start.setHours(timePart(dailyGuestFromHours, 0), timePart(dailyGuestFromMinutes, 0), 0, 0);
        end = new Date(start);
        end.setHours(timePart(dailyGuestToHours, 23), timePart(dailyGuestToMinutes, 59), 0, 0);
    } else {
        // Multi-day OR overnight-only mode: overnight times for both ends. When the
        // marina forbids same-day bookings and the user picked a same-day range,
        // extend the end to the next day.
        if (!dayBookingsEnabled && differenceInDays === 0) {
            end = addDays(start, 1);
        }
        start.setHours(timePart(overnightGuestFromHours, 0), timePart(overnightGuestFromMinutes, 0), 0, 0);
        end.setHours(timePart(overnightGuestToHours, 23), timePart(overnightGuestToMinutes, 59), 0, 0);
    }

    return { from: start, to: end };
}

/**
 * The time of day a reservation's "gültig bis" deadline defaults to: the marina's overnight
 * check-out time, falling back to 23:59. The deadline is a DATETIME and every Belegungsanzeige
 * treats the reservation as expired from that moment on, so a bare date would silently mean
 * "abgelaufen seit Mitternacht".
 */
export function toReservationValidUntilDefaultTime(
    unit: DefaultBerthReservationTimeUnit | null | undefined,
): { hours: number; minutes: number } {
    return {
        hours: timePart(unit?.overnightGuestToHours, 23),
        minutes: timePart(unit?.overnightGuestToMinutes, 59),
    };
}

/**
 * The time of day a typed arrival date defaults to: the marina's overnight check-in time,
 * falling back to midnight. Same-day ranges use the daily-guest times instead, which
 * {@link applyReservationDefaultTimes} re-applies once both ends are known.
 */
export function toReservationCheckInDefaultTime(
    unit: DefaultBerthReservationTimeUnit | null | undefined,
): { hours: number; minutes: number } {
    return {
        hours: timePart(unit?.overnightGuestFromHours, 0),
        minutes: timePart(unit?.overnightGuestFromMinutes, 0),
    };
}

/** Applies {@link toReservationValidUntilDefaultTime} to a date. Pure — the input is cloned. */
export function applyReservationValidUntilDefaultTime(
    date: Date,
    unit: DefaultBerthReservationTimeUnit | null | undefined,
): Date {
    const { hours, minutes } = toReservationValidUntilDefaultTime(unit);
    const result = new Date(date);

    result.setHours(hours, minutes, 0, 0);

    return result;
}

/** True when a picked value carries no time of day, i.e. the user only chose a date. */
export function isMidnight(date: Date): boolean {
    return date.getHours() === 0
        && date.getMinutes() === 0
        && date.getSeconds() === 0
        && date.getMilliseconds() === 0;
}
